/**
 * @ngdoc service
 * @name mx.SolutionBuilderAgent.PersonalizationService
 * @module mx.SolutionBuilderAgent
 *
 *
 * @description
 * Provides a functionality to get or save personalization settings
 */
PersonalizationService.$inject = [
	'mx.SolutionBuilderAgent.UserStorageService',
	'$stateParams',
	'$q',
	'mx.shell.AppsService',
	'mx.shell.EventService',
	'mx.shell.AuthBehaviorProviderService',
];

export function PersonalizationService(userStorageService, $stateParams, $q, appsService, eventService, authBehaviorProviderService) {
	let flushPayload;
	let storage;
	let storageInitializer;
	const storageHelpers = new Map();

	const gridPersonalizationKey = 'gridPersonalization';
	const groupingPersonalizationKey = 'groupingPersonalization';
	const selectedFiltersPersonalizationKey = 'selectedFiltersPersonalization';
	const groupingBoardPersonalizationKey = 'groupingBoardPersonalization';
	const defaultPreviewKey = 'defaultPreviewDialog';
	const defaultNewDialogKey = 'defaultNewDialog';
	const defaultEditDialogKey = 'defaultEditDialog';
	const favouriteViewsKey = 'favouriteViews';
	const favouriteNavigationsKey = 'favouriteNavigations';
	const recentViewsKey = 'recentViews';
	const defaultNavItemFilter = 'DefaultNavItemFilter';
	const calendarPersonalizationKey = 'calendarPersonalization';
	const attachmentsPersonalizationKey = 'attachmentsPersonalization';
	const navigationOrderingKey = 'navigationOrdering';
	const colorContrastPerThemeKey = 'colorContrastPerTheme';
	const gridColumnFiltersPinnedKey = 'gridColumnFiltersPinned';
	const filterPanelPinnedKey = 'filterPanelPinned';
	const filterPanelCloseAnimationKey = 'filterPanelCloseAnimation';
	const recentActionsKey = 'recentActions';
	const navigationSettingsKey = 'navigationSettingsKey';
	const themesKey = 'modernThemesPersonalization';
	const hintVisibilityKey = 'hintVisibility';
	const globalSearchKey = 'globalSearchKey';
	const notificationsKey = 'notificationsKey';
	const dashboardsKey = 'dashboardsKey';
	const sidePanelKey = 'sidePanelKey';

	const allPersonalizationKeys = [
		gridPersonalizationKey,
		groupingPersonalizationKey,
		selectedFiltersPersonalizationKey,
		defaultPreviewKey,
		defaultNewDialogKey,
		defaultEditDialogKey,
		favouriteViewsKey,
		recentViewsKey,
		groupingBoardPersonalizationKey,
		defaultNavItemFilter,
		calendarPersonalizationKey,
		favouriteNavigationsKey,
		attachmentsPersonalizationKey,
		navigationOrderingKey,
		colorContrastPerThemeKey,
		gridColumnFiltersPinnedKey,
		filterPanelPinnedKey,
		filterPanelCloseAnimationKey,
		recentActionsKey,
		navigationSettingsKey,
		themesKey,
		hintVisibilityKey,
		globalSearchKey,
		notificationsKey,
		dashboardsKey,
		sidePanelKey,
	];

	const queueFlush = _queueFlush();
	const queueFlushSlow = _queueFlushSlow();

	init();

	authBehaviorProviderService.onBeforeSessionDestroy(_flushData);

	// !!! "obsolete" warning
	function obsolete(method) {
		const messageParts = [
			`Method %c${method} %cis obsolete and will do nothing.`,
			'For integration with UUX use only methods provided in a Public Service ("mx.sb.public.service").',
			'Details on%c https://help.matrix42.com/030_DWP/030_INT/Business_Processes_and_API_Integrations/Public_API_reference_documentation/Front_End_Public_API',
		];

		console.error(messageParts.map((p) => `\t${p}`).join('\n\n'), 'font-weight: bold;', '', 'font-style: italic; font-weight: bold;');
	}

	return {
		getGridPersonalization: getGridPersonalization,
		getGroupingColumns: getGroupingColumns,
		getGroupingFilter: getGroupingFilter,
		reloadPersonalization: reloadPersonalization,
		saveGridPersonalization: saveGridPersonalization,
		savePersonalizedColumnVisibility: savePersonalizedColumnVisibility,
		savePersonalizedSorting: savePersonalizedSorting,
		savePersonalizedColumnPosition: savePersonalizedColumnPosition,
		savePersonalizedColumnSize: savePersonalizedColumnSize,
		savePersonalizedGrouping: savePersonalizedGrouping,
		savePersonalizedGroupingFilter: savePersonalizedGroupingFilter,
		getDefaultPreviewId,
		saveDefaultPreviewId,
		getDefaultPreviewDialogId: () => obsolete('getDefaultPreviewDialogId'),
		saveDefaultPreviewDialogId: () => obsolete('saveDefaultPreviewDialogId'),
		getDefaultPreviewDialogID: () => obsolete('getDefaultPreviewDialogID'),
		saveDefaultPreviewDialogID: () => obsolete('saveDefaultPreviewDialogID'),
		getDefaultNewDialogId,
		saveDefaultNewDialogId,
		getDefaultEditDialogId,
		saveDefaultEditDialogId,
		getGridPersonalizationStorageKeyByProfile: getGridPersonalizationStorageKeyByProfile,
		getRefreshInterval: getRefreshInterval,
		saveRefreshInterval: saveRefreshInterval,
		getRowHeight: getRowHeight,
		setRowHeight: setRowHeight,
		getPageSize: getPageSize,
		savePageSize: savePageSize,
		getFavouriteViews: getFavouriteViews,
		saveFavouriteViews: saveFavouriteViews,
		getFavouriteNavigations: getFavouriteNavigations,
		saveFavouriteNavigations: saveFavouriteNavigations,
		getRecentViews: getRecentViews,
		saveRecentViews: saveRecentViews,
		getRowsColoring: getRowsColoring,
		setRowsColoringAttribute: setRowsColoringAttribute,
		setColumnVisibility: setColumnVisibility,
		setQuickFilters: setQuickFilters,
		getQuickFilters: getQuickFilters,
		getBoardHorizontalGroupingField: getBoardHorizontalGroupingField,
		saveBoardHorizontalGroupingField: saveBoardHorizontalGroupingField,
		getBoardVisibleColumns: getBoardVisibleColumns,
		saveBoardVisibleColumns: saveBoardVisibleColumns,
		getBoardVisibleFields: getBoardVisibleFields,
		saveBoardVisibleFields: saveBoardVisibleFields,
		getBoardUseCustomTile: getBoardUseCustomTile,
		saveBoardUseCustomTile: saveBoardUseCustomTile,
		getCalendarSettings: getCalendarSettings,
		saveCalendarSettings: saveCalendarSettings,
		saveCalendarView: saveCalendarView,
		getAttachmentsSorting: getAttachmentsSorting,
		saveAttachmentsSorting: saveAttachmentsSorting,
		getAttachmentsViewMode: getAttachmentsViewMode,
		saveAttachmentsViewMode: saveAttachmentsViewMode,
		getAttachmentsSettings: getAttachmentsSettings,
		getEnhancedColorContrastMode: getEnhancedColorContrastMode,
		setEnhancedColorContrastMode: setEnhancedColorContrastMode,
		getGridColumnFiltersPinned: getGridColumnFiltersPinned,
		setGridColumnFiltersPinned: setGridColumnFiltersPinned,
		getFilterPanelPinned,
		setFilterPanelPinned,
		setFilterPanelCloseAnimationCount,
		getFilterPanelCloseAnimationCount,
		getSelectedFilters,
		saveSelectedFilters,
		getRecentActions,
		saveRecentActions,
		getNavigationHoverMode,
		setNavigationHoverMode,
		getNavigationPinned,
		setNavigationPinned,
		getNavigationCollapsed,
		setNavigationCollapsed,
		setNavigationWidth,
		getNavigationWidth,
		getUserThemeId,
		saveUserThemeId,
		getPlainStructureHintVisibility,
		setPlainStructureHintVisibility,
		getTryNewDashboardHintVisibility,
		setTryNewDashboardHintVisibility,
		getTryNewReportsHintVisibility,
		setTryNewReportsHintVisibility,
		getFilterClickHintVisibility,
		setFilterClickHintVisibility,
		getFilterToolbarCtrlKeyHintVisibility,
		setFilterToolbarCtrlKeyHintVisibility,
		getGlobalSearchHistory,
		setGlobalSearchHistory,
		setSideLayoutPinned,
		getSideLayoutPinned,
		getNotificationsStorage: async () => await createStorageHelper(notificationsKey, { processes: [], pinned: false, opened: false }),
		getDashboardsStorage: async () => await createStorageHelper(dashboardsKey, { dashboards: {}, hideRefreshPopover: false }),
	};

	function init() {
		flushPayload = {};
		storage = {};
		storageInitializer = null;
	}

	async function saveUserData(key, value) {
		flushPayload[key] = value;

		if ([colorContrastPerThemeKey, gridColumnFiltersPinnedKey, navigationSettingsKey, themesKey, hintVisibilityKey].includes(key)) {
			// instantly
			await _flushData();
		} else if ([favouriteViewsKey, favouriteNavigationsKey, defaultPreviewKey, defaultNewDialogKey, defaultEditDialogKey].includes(key)) {
			// in 1 second
			queueFlush();
		} else {
			// in 3 seconds
			queueFlushSlow();
		}
	}

	function reloadPersonalization(dataKey) {
		return userStorageService.getData([dataKey]).then(function (data) {
			try {
				const dataValue = data[dataKey];
				storage[dataKey] = dataValue ? JSON.parse(dataValue) : {};
				return storage[dataKey];
			} catch (e) {
				mx.console.warn(e);
			}
		});
	}

	function getStorage(key) {
		if (!storageInitializer) {
			storageInitializer = userStorageService.getData(allPersonalizationKeys).then(function (data) {
				storage = {};

				Object.keys(data).forEach(function (dataKey) {
					const dataValue = data[dataKey];
					storage[dataKey] = dataValue ? JSON.parse(dataValue) : {};
				});

				return storage;
			});
		}

		return storageInitializer.then(function (userStorage) {
			if (!userStorage[key]) {
				userStorage[key] = {};
			}

			return userStorage[key];
		});
	}

	// TODO: move global search personalization to service
	function getGridPersonalizationStorageKeyByProfile(contextKey, userProfile) {
		return _getGridPersonalizationStorageKeyForProfile(contextKey)(userProfile);
	}

	function _getGridPersonalizationStorageKeyForProfile(contextKey) {
		return function (userProfile) {
			return 'SearchPage::Grid::User_' + userProfile.id + '::DataQuery_' + contextKey;
		};
	}

	function _getGridPersonalizationStorageKey(contextKey) {
		return $q.when('SearchPage_' + contextKey);
	}

	function getGridPersonalization(contextKey) {
		return _getGridPersonalizationStorageKey(contextKey).then(function (storageKey) {
			return getStorage(gridPersonalizationKey).then(function (userGridStorage) {
				return userGridStorage[storageKey] || {};
			});
		});
	}

	function getFilteredGridPersonalization(contextKey) {
		return _getGridPersonalizationStorageKey(contextKey).then(function (storageKey) {
			return getStorage(gridPersonalizationKey).then(function (userGridStorage) {
				return userGridStorage[storageKey] || {};
			});
		});
	}

	function saveGridPersonalization(personalization, contextKey) {
		return _getGridPersonalizationStorageKey(contextKey).then(function (storageKey) {
			return getStorage(gridPersonalizationKey).then(function (userGridStorage) {
				userGridStorage[storageKey] = personalization;
				saveUserData(gridPersonalizationKey, userGridStorage);

				eventService.dispatchEvent(mx.SolutionBuilderAgent.Events.GridPersonalizationSaved, {
					contextKey: contextKey,
					personalization: personalization,
				});
			});
		});
	}

	function saveFilteredGridPersonalization(personalization, contextKey) {
		return _getGridPersonalizationStorageKey(contextKey).then(function (storageKey) {
			return getStorage(gridPersonalizationKey).then(function (userGridStorage) {
				userGridStorage[storageKey] = personalization;
				saveUserData(gridPersonalizationKey, userGridStorage);
			});
		});
	}

	function savePersonalizedColumnVisibility(column, contextKey) {
		return getGridPersonalization(contextKey).then(function (personalization) {
			personalization[column.name] = personalization[column.name] || {};
			personalization[column.name].IsDisplay = column.visible;
			saveGridPersonalization(personalization, contextKey);
		});
	}

	function savePersonalizedSorting(columns, contextKey) {
		getGridPersonalization(contextKey).then(function (personalization) {
			// Clean old sorting if set
			for (const k in personalization) {
				if (personalization.hasOwnProperty(k)) {
					delete personalization[k].Sorting;
					delete personalization[k].SortingPriority;
				}
			}
			if (Array.isArray(columns) && columns.length) {
				columns.forEach(function (column) {
					personalization[column.name] = personalization[column.name] || {};
					personalization[column.name].Sorting = column.sorting;
					personalization[column.name].SortingPriority = column.priority;
				});
			}
			saveGridPersonalization(personalization, contextKey);
		});
	}

	function savePersonalizedColumnPosition(columns, contextKey) {
		getGridPersonalization(contextKey).then(function (personalization) {
			columns.forEach(function (col, i) {
				personalization[col.name] = personalization[col.name] || {};
				personalization[col.name].__personalizedPosition = i;
			});
			saveGridPersonalization(personalization, contextKey);
		});
	}

	function savePersonalizedColumnSize(column, absoluteWidth, contextKey, classicWidth) {
		getGridPersonalization(contextKey).then(function (personalization) {
			personalization[column.name] = personalization[column.name] || {};
			personalization[column.name].Width = classicWidth;
			saveGridPersonalization(personalization, contextKey);
		});
	}

	function saveGroupingPersonalization(personalization, contextKey) {
		return _getGridPersonalizationStorageKey(contextKey).then(function (storageKey) {
			return getStorage(groupingPersonalizationKey).then(function (userGroupingStorage) {
				userGroupingStorage[storageKey] = personalization;
				saveUserData(groupingPersonalizationKey, userGroupingStorage);
			});
		});
	}

	function savePersonalizedGrouping(columns, contextKey) {
		return getGroupingPersonalization(contextKey).then(function (personalization) {
			personalization.__groupingBy__ = columns.map(function (c) {
				return {
					Name: c.Name,
					SortDirection: c.SortDirection,
				};
			});
			saveGroupingPersonalization(personalization, contextKey);
		});
	}

	function savePersonalizedGroupingFilter(groupingFilter, contextKey) {
		return getGroupingPersonalization(contextKey).then(function (personalization) {
			personalization.__groupingFilter__ = groupingFilter || [];
			saveGroupingPersonalization(personalization, contextKey);
		});
	}

	function getGroupingPersonalization(contextKey) {
		return _getGridPersonalizationStorageKey(contextKey).then(function (storageKey) {
			return getStorage(groupingPersonalizationKey).then(function (userGroupingStorage) {
				return userGroupingStorage[storageKey] || {};
			});
		});
	}

	function getGroupingColumns(contextKey) {
		return getGroupingPersonalization(contextKey).then(function (res) {
			return res.__groupingBy__;
		});
	}

	function getGroupingFilter(contextKey) {
		return getGroupingPersonalization(contextKey).then(function (res) {
			return res.__groupingFilter__;
		});
	}

	/* START  BOARD personalization */

	function getBoardGroupingPersonalization(contextKey) {
		return _getGridPersonalizationStorageKey(contextKey).then(function (storageKey) {
			return getStorage(groupingBoardPersonalizationKey).then(function (userGroupingBoardStorage) {
				return userGroupingBoardStorage[storageKey] || {};
			});
		});
	}

	function saveGroupingBoardPersonalization(personalization, contextKey) {
		return _getGridPersonalizationStorageKey(contextKey).then(function (storageKey) {
			return getStorage(groupingBoardPersonalizationKey).then(function (userGroupingBoardStorage) {
				userGroupingBoardStorage[storageKey] = personalization;
				saveUserData(groupingBoardPersonalizationKey, userGroupingBoardStorage);
			});
		});
	}

	function getBoardHorizontalGroupingField(contextKey) {
		return getBoardGroupingPersonalization(contextKey).then(function (res) {
			return res.horizontalGroupingField;
		});
	}

	function saveBoardHorizontalGroupingField(fieldName, contextKey) {
		return getBoardGroupingPersonalization(contextKey).then(function (personalization) {
			personalization.horizontalGroupingField = fieldName;
			saveGroupingBoardPersonalization(personalization, contextKey);
		});
	}

	function getBoardVisibleColumns(contextKey, horizontalGroupingField) {
		return getBoardGroupingPersonalization(contextKey).then(function (res) {
			return ((res.fields || {})[horizontalGroupingField] || {}).visibleColumns;
		});
	}

	function saveBoardVisibleColumns(columns, horizontalGroupingField, contextKey) {
		return getBoardGroupingPersonalization(contextKey).then(function (personalization) {
			personalization.fields = personalization.fields || {};
			personalization.fields[horizontalGroupingField] = personalization.fields[horizontalGroupingField] || {};
			personalization.fields[horizontalGroupingField].visibleColumns = columns;
			saveGroupingBoardPersonalization(personalization, contextKey);
		});
	}

	function getBoardVisibleFields(contextKey) {
		return getBoardGroupingPersonalization(contextKey).then(function (res) {
			return res.visibleFields || {};
		});
	}

	function saveBoardVisibleFields(visibleFields, contextKey) {
		return getBoardGroupingPersonalization(contextKey).then(function (personalization) {
			personalization.visibleFields = visibleFields;
			saveGroupingBoardPersonalization(personalization, contextKey);
		});
	}

	function getBoardUseCustomTile(contextKey) {
		return getBoardGroupingPersonalization(contextKey).then(function (res) {
			return res.useCustomTile || false;
		});
	}

	function saveBoardUseCustomTile(useCustomTile, contextKey) {
		return getBoardGroupingPersonalization(contextKey).then(function (personalization) {
			personalization.useCustomTile = useCustomTile;
			saveGroupingBoardPersonalization(personalization, contextKey);
		});
	}

	/* END  BOARD personalization */

	/* START  CALENDAR personalization */
	function getCalendarSettings(contextKey) {
		return _getAllCalendarsSettings().then(function (allCalendarsSettings) {
			return allCalendarsSettings[contextKey] || {};
		});
	}

	function saveCalendarSettings(contextKey, settings) {
		return _getAllCalendarsSettings().then(function (allCalendarsSettings) {
			// Clean-up of junk
			allCalendarsSettings[contextKey] = {
				groupByExtraDimension: settings.groupByExtraDimension,
				extraDimensionValuesVisibility: settings.extraDimensionValuesVisibility,
				calendarView: settings.calendarView,
				showUnassigned: settings.showUnassigned,
			};
			saveUserData(calendarPersonalizationKey, allCalendarsSettings);
		});
	}

	function saveCalendarView(contextKey, view) {
		return _getAllCalendarsSettings().then(function (allCalendarsSettings) {
			if (!allCalendarsSettings[contextKey]) {
				allCalendarsSettings[contextKey] = {};
			}
			allCalendarsSettings[contextKey].calendarView = view;
			saveUserData(calendarPersonalizationKey, allCalendarsSettings);
		});
	}

	function _getAllCalendarsSettings() {
		return getStorage(calendarPersonalizationKey).then(function (settings) {
			return settings || {};
		});
	}

	/* END CALENDAR personalization */

	function _getDefaultDialogStoreKey(entityType) {
		return $q.when('EntityType_' + entityType);
	}

	async function getDefaultDialogId(key, entityType) {
		const storageKey = await _getDefaultDialogStoreKey(entityType);
		const dialogStorage = await getStorage(key);
		return dialogStorage[storageKey] || null;
	}

	async function saveDefaultDialogId(key, entityType, dialogID) {
		const storageKey = await _getDefaultDialogStoreKey(entityType);
		const dialogStorage = await getStorage(key);
		dialogStorage[storageKey] = dialogID;
		saveUserData(key, dialogStorage);
	}

	async function getDefaultPreviewId(entityType) {
		return await getDefaultDialogId(defaultPreviewKey, entityType);
	}

	async function saveDefaultPreviewId(entityType, dialogID) {
		await saveDefaultDialogId(defaultPreviewKey, entityType, dialogID);
	}

	async function getDefaultNewDialogId(entityType) {
		return await getDefaultDialogId(defaultNewDialogKey, entityType);
	}

	async function saveDefaultNewDialogId(entityType, dialogID) {
		await saveDefaultDialogId(defaultNewDialogKey, entityType, dialogID);
	}

	async function getDefaultEditDialogId(entityType) {
		return await getDefaultDialogId(defaultEditDialogKey, entityType);
	}

	async function saveDefaultEditDialogId(entityType, dialogID) {
		await saveDefaultDialogId(defaultEditDialogKey, entityType, dialogID);
	}

	function getRefreshInterval(contextKey) {
		return getGridPersonalization(contextKey).then(function (personalization) {
			return personalization.refreshInterval;
		});
	}

	function saveRefreshInterval(interval, contextKey) {
		getGridPersonalization(contextKey).then(function (personalization) {
			personalization.refreshInterval = interval;
			saveGridPersonalization(personalization, contextKey);
		});
	}

	function getRowHeight() {
		return getAllGridsAttribute('modern_rowHeight');
	}

	function setRowHeight(rowHeight) {
		setAllGridsAttribute('modern_rowHeight', rowHeight);
	}

	function getPageSize() {
		return getAllGridsAttribute('pageSize');
	}

	function savePageSize(pageSize) {
		setAllGridsAttribute('pageSize', pageSize);
	}

	function setRowsColoringAttribute(name, value) {
		setAllGridsAttribute(name, value);
	}

	function setColumnVisibility(contextKey, columnName, isVisible) {
		return getGridPersonalization(contextKey).then(function (personalization) {
			let column = personalization[columnName];
			if (!column) {
				column = {};
				personalization[columnName] = column;
			}

			column.visible = isVisible;
			saveGridPersonalization(personalization, contextKey);
		});
	}

	function setQuickFilters(contextKey, quickFilters) {
		return getFilteredGridPersonalization(contextKey).then(function (personalization) {
			personalization.quickFilters = quickFilters;
			saveFilteredGridPersonalization(personalization, contextKey);
		});
	}

	function getQuickFilters(contextKey) {
		return getFilteredGridPersonalization(contextKey).then(function (personalization) {
			return personalization.quickFilters;
		});
	}

	function setAllGridsAttribute(name, value) {
		getGridPersonalization('AllGrids').then(function (personalization) {
			personalization[name] = value;
			saveGridPersonalization(personalization, 'AllGrids');
		});
	}

	function getRowsColoring() {
		return getGridPersonalization('AllGrids').then(function (personalization) {
			return {
				enabled: personalization.rowColoringEnabled,
			};
		});
	}

	function getAllGridsAttribute(name) {
		return getGridPersonalization('AllGrids').then(function (personalization) {
			return personalization[name];
		});
	}

	function getFavouriteViewsStorageKey() {
		return $q.when('FavouriteViews::Current');
	}

	/**
	 * @returns {Promise<ViewObjectDetails[]>}
	 */
	function getFavouriteViews() {
		return getFavouriteViewsStorageKey().then(function (storageKey) {
			return getStorage(favouriteViewsKey).then(function (userFavouriteViewsStorage) {
				const app = appsService.getCurrentApplication();
				return (userFavouriteViewsStorage[storageKey] && userFavouriteViewsStorage[storageKey][app.value]) || [];
			});
		});
	}

	/**
	 * @param {ViewObjectDetails[]} favouriteViews
	 */
	function saveFavouriteViews(favouriteViews) {
		return getFavouriteViewsStorageKey().then(function (storageKey) {
			return getStorage(favouriteViewsKey).then(function (userFavouriteViewsStorage) {
				const app = appsService.getCurrentApplication();
				if (Array.isArray(userFavouriteViewsStorage[storageKey]) || typeof userFavouriteViewsStorage[storageKey] !== 'object') {
					userFavouriteViewsStorage[storageKey] = {};
				}
				userFavouriteViewsStorage[storageKey][app.value] = favouriteViews;
				saveUserData(favouriteViewsKey, userFavouriteViewsStorage);
			});
		});
	}

	function getFavouriteNavigationsStorageKey() {
		return $q.when('FavouriteNavigations::Current');
	}

	/**
	 * @returns {Promise<string[]>}
	 */
	function getFavouriteNavigations() {
		return getFavouriteNavigationsStorageKey().then(function (storageKey) {
			return getStorage(favouriteNavigationsKey).then(function (userFavouriteNavigationsStorage) {
				const app = appsService.getCurrentApplication();
				return (userFavouriteNavigationsStorage[storageKey] && userFavouriteNavigationsStorage[storageKey][app.value]) || [];
			});
		});
	}

	function saveFavouriteNavigations(favouriteNavigations) {
		return getFavouriteNavigationsStorageKey().then(function (storageKey) {
			return getStorage(favouriteNavigationsKey).then(function (userFavouriteNavigationsStorage) {
				const app = appsService.getCurrentApplication();
				if (Array.isArray(userFavouriteNavigationsStorage[storageKey]) || typeof userFavouriteNavigationsStorage[storageKey] !== 'object') {
					userFavouriteNavigationsStorage[storageKey] = {};
				}
				userFavouriteNavigationsStorage[storageKey][app.value] = favouriteNavigations;
				saveUserData(favouriteNavigationsKey, userFavouriteNavigationsStorage);
			});
		});
	}

	function getRecentViewsStorageKey() {
		return $q.when('RecentViews::Current');
	}

	function getRecentViews() {
		return getRecentViewsStorageKey().then(function (storageKey) {
			return getStorage(recentViewsKey).then(function (userRecentViewsStorage) {
				const app = appsService.getCurrentApplication();
				return (userRecentViewsStorage[storageKey] && userRecentViewsStorage[storageKey][app.value]) || [];
			});
		});
	}

	function saveRecentViews(recentViews) {
		return getRecentViewsStorageKey().then(function (storageKey) {
			return getStorage(recentViewsKey).then(function (userRecentViewsStorage) {
				const app = appsService.getCurrentApplication();
				if (Array.isArray(userRecentViewsStorage[storageKey]) || typeof userRecentViewsStorage[storageKey] !== 'object') {
					userRecentViewsStorage[storageKey] = {};
				}
				userRecentViewsStorage[storageKey][app.value] = recentViews;
				saveUserData(recentViewsKey, userRecentViewsStorage);
			});
		});
	}

	async function getSelectedFilters(contextKey) {
		const storage = await getStorage(selectedFiltersPersonalizationKey);
		const contextStorage = storage[contextKey];
		return (
			contextStorage && {
				// backward compatibility
				favorite: contextStorage.selected,
				selected: contextStorage.active,
			}
		);
	}

	async function saveSelectedFilters(contextKey, filters) {
		const storage = await getStorage(selectedFiltersPersonalizationKey);
		storage[contextKey] = {
			// backward compatibility
			selected: filters.filter((filter) => filter.favorite).map((filter) => filter.id),
			active: filters.filter((filter) => filter.favorite && filter.selected).map((filter) => filter.id),
		};
		saveUserData(selectedFiltersPersonalizationKey, storage);
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.PersonalizationService#getRecentActions
	 *
	 * @description
	 * Get personalized recent actions
	 *
	 * @returns {Promise} Promise resolved as objects per each widget
	 */
	async function getRecentActions() {
		return await getStorage(recentActionsKey);
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.PersonalizationService#saveRecentActions
	 *
	 * @description
	 * Save personalized recent actions for each widget
	 *
	 * @param {object} selectedFilters -- personalized settings of recent actions
	 *
	 */
	function saveRecentActions(selectedFilters) {
		saveUserData(recentActionsKey, selectedFilters);
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.PersonalizationService#getAttachmentsSettings
	 *
	 * @description
	 * Get personalized settings for attachments
	 *
	 * @returns {Promise} Promise resolved as settings objects
	 */
	function getAttachmentsSettings() {
		return getStorage(attachmentsPersonalizationKey).then((settings) => settings || {});
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.PersonalizationService#saveAttachmentsSettings
	 *
	 * @description
	 * Save personalized settings for attachments
	 *
	 * @param {object} settings - personalized settings of attachments
	 * @param {string} settings.sortBy - field name which is used for sorting
	 * @param {string} settings.viewMode - view mode of attachments control
	 */
	function saveAttachmentsSettings(settings) {
		saveUserData(attachmentsPersonalizationKey, settings);
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.PersonalizationService#getAttachmentsSorting
	 *
	 * @description
	 * Get personalized field name which is used for sorting in attachments
	 *
	 * @returns {Promise} Promise resolved as field name
	 */
	function getAttachmentsSorting() {
		return getAttachmentsSettings().then((personalization) => personalization.sortBy);
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.PersonalizationService#getAttachmentsViewMode
	 *
	 * @description
	 * Get personalized view mode in attachments
	 *
	 * @returns {Promise} Promise resolved as view mode
	 */
	function getAttachmentsViewMode() {
		return getAttachmentsSettings().then((personalization) => personalization.viewMode);
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.PersonalizationService#saveAttachmentsSorting
	 *
	 * @description
	 * Save personalized field name which is used for sorting in attachments
	 *
	 * @param {string} sortBy - field name which is used for sorting
	 *
	 * @returns {Promise} Promise resolved when data was saved
	 */
	function saveAttachmentsSorting(sortBy) {
		return getAttachmentsSettings().then((personalization) => {
			personalization.sortBy = sortBy;
			saveAttachmentsSettings(personalization);
		});
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.PersonalizationService#saveAttachmentsViewMode
	 *
	 * @description
	 * Save personalized view mode in attachments
	 *
	 * @param {string} viewMode - view mode of attachments control
	 *
	 * @returns {Promise} Promise resolved when data was saved
	 */
	function saveAttachmentsViewMode(viewMode) {
		return getAttachmentsSettings().then((personalization) => {
			personalization.viewMode = viewMode;
			saveAttachmentsSettings(personalization);
		});
	}

	async function setEnhancedColorContrastMode(themeId, value) {
		const storage = await getStorage(colorContrastPerThemeKey);
		storage[themeId] = value;
		saveUserData(colorContrastPerThemeKey, storage);
	}

	async function getEnhancedColorContrastMode(themeId) {
		const storage = await getStorage(colorContrastPerThemeKey);
		return storage[themeId];
	}

	function setGridColumnFiltersPinned(value) {
		saveUserData(gridColumnFiltersPinnedKey, { enabled: !!value });
	}

	async function getGridColumnFiltersPinned() {
		return (await getStorage(gridColumnFiltersPinnedKey)).enabled;
	}

	async function setFilterPanelPinned(value) {
		const storage = await getStorage(filterPanelPinnedKey);
		storage.enabled = !!value;
		saveUserData(filterPanelPinnedKey, { enabled: storage.enabled });
	}

	async function getFilterPanelPinned() {
		const val = (await getStorage(filterPanelPinnedKey)).enabled;
		return typeof val === 'undefined' ? true : val;
	}

	async function setFilterPanelCloseAnimationCount(value) {
		const storage = await getStorage(filterPanelCloseAnimationKey);
		storage.count = +value;
		saveUserData(filterPanelCloseAnimationKey, { count: storage.count });
	}

	async function getFilterPanelCloseAnimationCount() {
		const val = (await getStorage(filterPanelCloseAnimationKey)).count;
		return typeof val === 'undefined' ? 0 : val;
	}

	async function getGlobalSearchSettings() {
		return (await getStorage(globalSearchKey)) || {};
	}

	async function setGlobalSearchSettings(settings) {
		saveUserData(globalSearchKey, settings);
	}

	async function updateGlobalSearchSettings(newSettings) {
		await setGlobalSearchSettings(Object.assign(await getGlobalSearchSettings(), newSettings));
	}

	async function setGlobalSearchHistory(value) {
		await updateGlobalSearchSettings({ history: value });
	}

	async function getGlobalSearchHistory() {
		return (await getGlobalSearchSettings()).history || [];
	}

	async function getNavigationSettings() {
		return (await getStorage(navigationSettingsKey)) || {};
	}

	async function setNavigationSettings(settings) {
		saveUserData(navigationSettingsKey, settings);
	}

	async function updateNavigationSettings(newSettings) {
		await setNavigationSettings(Object.assign({}, await getNavigationSettings(), newSettings));
	}

	async function setNavigationPinned(value) {
		await updateNavigationSettings({ pinned: value });
	}

	async function getNavigationPinned() {
		return (await getNavigationSettings()).pinned;
	}

	async function setNavigationHoverMode(value) {
		await updateNavigationSettings({ hoverMode: value });
	}

	async function getNavigationHoverMode() {
		return (await getNavigationSettings()).hoverMode;
	}

	async function setNavigationCollapsed(value) {
		await updateNavigationSettings({ collapsed: value });
	}

	async function getNavigationCollapsed() {
		return (await getNavigationSettings()).collapsed;
	}

	async function setNavigationWidth(value) {
		await updateNavigationSettings({ width: value });
	}

	async function getNavigationWidth() {
		return (await getNavigationSettings()).width;
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.PersonalizationService#getUserThemeId
	 * @async
	 *
	 * @description
	 * Get user theme id
	 *
	 * @returns {Promise<string | undefined>} theme id if it was saved
	 */
	async function getUserThemeId() {
		const storage = await getStorage(themesKey);
		return storage.themeId;
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.PersonalizationService#saveUserThemeId
	 * @async
	 *
	 * @param {string | undefined} themeId - user theme id to save or undefined if personalization should be removed
	 *
	 * @description
	 * Save peronalized user theme id
	 *
	 * @returns {Promise<void>} Promise resolved when data was saved
	 */
	async function saveUserThemeId(themeId) {
		const storage = await getStorage(themesKey);
		storage.themeId = themeId;
		await saveUserData(themesKey, { themeId });
	}

	async function getHintVisibilityStorage() {
		return await getStorage(hintVisibilityKey);
	}

	async function updateHintVisibilityStorage(key, value) {
		const storage = await getStorage(hintVisibilityKey);
		storage[key] = value;
		await saveUserData(hintVisibilityKey, storage);
	}

	async function getPlainStructureHintVisibility() {
		const storage = await getHintVisibilityStorage();
		return typeof storage.plainStructureHintVisibility === 'undefined' ? true : storage.plainStructureHintVisibility;
	}

	async function setPlainStructureHintVisibility(visible) {
		await updateHintVisibilityStorage('plainStructureHintVisibility', visible);
	}

	async function getTryNewDashboardHintVisibility() {
		const storage = await getHintVisibilityStorage();
		return typeof storage.tryNewDashboardHintVisibility === 'undefined' ? true : storage.tryNewDashboardHintVisibility;
	}

	async function setTryNewDashboardHintVisibility(visible) {
		await updateHintVisibilityStorage('tryNewDashboardHintVisibility', visible);
	}

	async function getTryNewReportsHintVisibility() {
		const storage = await getHintVisibilityStorage();
		return typeof storage.tryNewReportsHintVisibility === 'undefined' ? true : storage.tryNewReportsHintVisibility;
	}

	async function setTryNewReportsHintVisibility(visible) {
		await updateHintVisibilityStorage('tryNewReportsHintVisibility', visible);
	}

	async function getFilterClickHintVisibility() {
		const storage = await getHintVisibilityStorage();
		return typeof storage.filterClickHintVisibility === 'undefined' ? true : storage.filterClickHintVisibility;
	}

	async function setFilterClickHintVisibility(visible) {
		await updateHintVisibilityStorage('filterClickHintVisibility', visible);
	}

	async function getFilterToolbarCtrlKeyHintVisibility() {
		const storage = await getHintVisibilityStorage();
		return typeof storage.filterToolbarCtrlKeyHintVisibility === 'undefined' ? true : storage.filterToolbarCtrlKeyHintVisibility;
	}

	async function setFilterToolbarCtrlKeyHintVisibility(visible) {
		await updateHintVisibilityStorage('filterToolbarCtrlKeyHintVisibility', visible);
	}

	async function getStorageHelper(key, initialValue) {
		let storageHelper = storageHelpers.get(key);
		if (!storageHelper) {
			storageHelper = await createStorageHelper(sidePanelKey, initialValue);
			storageHelpers.set(key, storageHelper);
		}
		return storageHelper;
	}

	async function getSideLayoutStorage() {
		return await getStorageHelper(sidePanelKey, { pinned: false });
	}

	async function getSideLayoutPinned() {
		const storage = await getSideLayoutStorage();
		return storage.getValue('pinned');
	}

	async function setSideLayoutPinned(pinned) {
		const storage = await getSideLayoutStorage();
		await storage.setValue('pinned', pinned);
	}

	async function createStorageHelper(storageKey, initialValue) {
		if (!allPersonalizationKeys.includes(storageKey)) {
			allPersonalizationKeys.push(storageKey);
		}
		let storage = await getStorage(storageKey);
		return {
			getValue(key) {
				return storage[key] || initialValue?.[key];
			},
			async setValue(key, value) {
				storage[key] = value;
				await saveUserData(storageKey, storage);
			},
			async updateStorage(updates) {
				await saveUserData(Object.assign(storage, updates));
			},
			async reload() {
				storage = await reloadPersonalization(storageKey);
			},
		};
	}

	function _flushData() {
		if (flushPayload && Object.keys(flushPayload).length) {
			const promise = userStorageService.setData(flushPayload);
			flushPayload = {};
			return promise;
		}
	}

	function _queueFlush() {
		return $.debounce(1000, function () {
			_flushData();
		});
	}

	function _queueFlushSlow() {
		return $.debounce(3000, function () {
			_flushData();
		});
	}
}
